import { Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import clsx from "clsx";
import React, { FC, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import { LearningFailure, LearningStageContent, useLearningLayout } from "@/components/learning/LearningStageContent";
import { Loading } from "@/components/Loading";
import { findArticleById, findStageById } from "@/constants/learning";
import { fontFamilies, layoutTokens } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { useLearningProgress } from "@/hooks/useLearningProgress";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";
import { AppEventTokenEnum, sendEventToAppMeasurementTool } from "@/utilities/capacitorLogic";
import { isAllowedUrl, navigateWithinApp } from "@/utilities/navigation";
import { AGENT_DETAIL_URL_SOURCE } from "@/utilities/userOffer";

import { ArticleScrollModal } from "./components/ArticleScrollModal";
import { LearningArticleBody } from "./components/LearningArticleBody";
import { articleBodies } from "./data/articleBodies";

const color = theme.palette.semantic;
const useStyles = makeStyles(() => ({
  article: { display: "flex", flexDirection: "column", gap: theme.spacing(3), minWidth: 0 },
  label: { fontSize: 12, fontWeight: 700, color: theme.palette.text.secondary },
  segments: { display: "flex", gap: theme.spacing(0.5), margin: theme.spacing(1, 0, 1.5) },
  segment: { height: 4, flex: 1, backgroundColor: color["border/default"], borderRadius: layoutTokens.radius.full },
  previous: { backgroundColor: alpha(color["action/primary"], 0.4) },
  current: { backgroundColor: color["action/primary"] },
  number: { fontFamily: fontFamilies.number, fontSize: 14, lineHeight: 1 },
  tail: {
    margin: theme.spacing(0, -3, -4),
    padding: theme.spacing(3, 3, 4),
    backgroundColor: color["surface/base"],
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    [theme.breakpoints.up("md")]: { margin: 0, padding: 0 },
  },
  tailCards: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr)",
    gap: theme.spacing(1.5),
    [theme.breakpoints.up("md")]: { gridTemplateColumns: "repeat(2,minmax(0,1fr))" },
  },
  tailCard: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(1.5),
    textAlign: "left",
    padding: theme.spacing(2),
    borderRadius: layoutTokens.radius.lg,
    backgroundColor: color["surface/card"],
    boxShadow: `0 2px 8px ${alpha(color["text/primary"], 0.06)}`,
    "&.Mui-disabled": { opacity: 1 },
  },
  circle: {
    width: 24,
    height: 24,
    borderRadius: layoutTokens.radius.full,
    border: `1.5px solid ${color["border/default"]}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  check: {
    backgroundColor: color["status/success"],
    borderColor: color["status/success"],
    color: color["text/inverse"],
  },
  cardTitle: { fontSize: 14, fontWeight: 500, lineHeight: 1.3, overflowWrap: "anywhere" },
  cardHint: { fontSize: 12, color: theme.palette.text.secondary, marginTop: theme.spacing(0.5) },
  ask: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 2),
    borderRadius: layoutTokens.radius.md,
    color: color["text/inverse"],
    backgroundColor: color["surface/trust"],
    [theme.breakpoints.up("md")]: { padding: theme.spacing(3), borderRadius: layoutTokens.radius.lg },
  },
  eyebrow: { fontSize: 12, fontWeight: 700, [theme.breakpoints.up("md")]: { fontSize: 14 } },
  askTitle: { fontSize: 16, fontWeight: 700 },
  askDescription: { fontSize: 12, opacity: 0.8, [theme.breakpoints.up("md")]: { fontSize: 14 } },
  askButton: { maxWidth: "none", [theme.breakpoints.up("md")]: { width: 240, margin: "0 !important" } },
}));

export const LearningArticlePage: FC = () => {
  const layout = useLearningLayout();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation<{ fromStage?: string }>();
  const { articleId } = useParams<{ articleId: string }>();
  const { isMobile } = useDevice();
  const { statusMap, initialized, loading, error, refetch, markAsCompleted } = useLearningProgress();
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isLeadModalOpen, setLeadModalOpen] = useState(false);
  const activeArticleRef = useRef(articleId);
  activeArticleRef.current = articleId;
  useEffect(() => {
    setLeadModalOpen(false);
    setSaveError(null);
  }, [articleId]);
  const article = findArticleById(articleId);
  const stage = article && findStageById(article.stageId);
  const fromStage = location.state?.fromStage ?? article?.stageId;
  const handleBack = () => history.replace(fromStage ? `/learning/${fromStage}` : "/learning");
  const openArticle = (id: string) => {
    setLeadModalOpen(false);
    setSaveError(null);
    history.push({ pathname: `/learning/articles/${id}`, state: { fromStage: stage?.id } });
    window.scrollTo(0, 0);
  };
  const openConsultation = () => {
    sendEventToAppMeasurementTool(AppEventTokenEnum.ClickLearningArticleCta);
    window.sessionStorage.setItem("agent_search_source", AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE);
    setLeadModalOpen(true);
  };
  const complete = async () => {
    if (!article || savingRef.current || statusMap[article.id]) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      await markAsCompleted(article.id);
    } catch {
      if (activeArticleRef.current === article.id) setSaveError("学習状況の更新に失敗しました。再度お試しください。");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  if (!article || !stage || !articleBodies[article.id] || !isAllowedUrl(article.url))
    return (
      <BaseTemplate headerTitle="学ぶ" onBack={handleBack} isBackGroundWhite>
        <Box className={layout.state}>
          <Typography>指定された記事が見つかりませんでした。</Typography>
          <CustomButton onClick={handleBack}>学習トップへ戻る</CustomButton>
        </Box>
      </BaseTemplate>
    );
  const index = stage.articles.findIndex((item) => item.id === article.id);
  const next = stage.articles[index + 1];
  const status = statusMap[article.id];
  const date = status?.completed_at ? new Date(status.completed_at) : null;
  const dateLabel =
    date && !Number.isNaN(date.getTime())
      ? `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日に読了`
      : "読了済み";
  return (
    <BaseTemplate
      isDense
      isPcV2
      isBackGroundWhite={isMobile}
      headerTitle={isMobile ? stage.label : undefined}
      onBack={isMobile ? handleBack : undefined}
      disableBottomPadding
    >
      <Box className={layout.page}>
        {!isMobile && (
          <Box className={layout.head}>
            <ButtonBase className={layout.back} onClick={() => history.replace("/learning")}>
              <Chevron direction="back" />
              不動産投資を学ぶ
            </ButtonBase>
            <Typography component="h1" className={layout.title}>
              {article.title}
            </Typography>
          </Box>
        )}
        <Box className={layout.columns}>
          {!isMobile && (
            <Box>
              {loading || !initialized ? (
                <Loading />
              ) : error && error.includes("取得") ? (
                <LearningFailure error={error} onRetry={refetch} />
              ) : (
                <LearningStageContent
                  compact
                  stage={stage}
                  statusMap={statusMap}
                  currentArticleId={article.id}
                  onOpenArticle={(item) => openArticle(item.id)}
                />
              )}
            </Box>
          )}
          <Box className={classes.article}>
            {isMobile && (
              <Box>
                <Typography className={classes.label}>{stage.label}</Typography>
                <Box className={classes.segments}>
                  {stage.articles.map((item, position) => (
                    <span
                      key={item.id}
                      className={clsx(
                        classes.segment,
                        position < index && classes.previous,
                        position === index && classes.current
                      )}
                    />
                  ))}
                </Box>
                <Typography className={classes.number}>
                  {index + 1} / {stage.articles.length}章
                </Typography>
                <Box mt={3}>
                  <Typography component="h1" className={layout.title}>
                    {article.title}
                  </Typography>
                </Box>
              </Box>
            )}
            <LearningArticleBody
              nodes={articleBodies[article.id].nodes}
              onNavigate={(href) => {
                const target = new URL(href);
                const linked = findArticleById(target.pathname.match(/\/(?:articles\/)?lp\/([^/]+)/)?.[1] ?? "");
                if (linked) openArticle(linked.id);
                else {
                  if (target.pathname.startsWith("/agent_search")) {
                    sendEventToAppMeasurementTool(AppEventTokenEnum.ClickLearningArticleCta);
                    window.sessionStorage.setItem("agent_search_source", AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE);
                    target.searchParams.set("source", AGENT_DETAIL_URL_SOURCE.LEARNING_ARTICLE);
                  }
                  navigateWithinApp(history, target);
                }
              }}
            />
            <Box className={classes.tail}>
              {isMobile && error && error.includes("取得") && <LearningFailure error={error} onRetry={refetch} />}
              {saveError && (
                <Typography role="alert" color="error">
                  {saveError}
                </Typography>
              )}
              <Box className={classes.tailCards}>
                <ButtonBase
                  className={classes.tailCard}
                  disabled={Boolean(status) || saving || loading || !initialized || Boolean(error?.includes("取得"))}
                  onClick={complete}
                >
                  <span className={clsx(classes.circle, status && classes.check)}>
                    {status && <CheckIcon style={{ fontSize: 12 }} />}
                  </span>
                  <Box>
                    <Typography className={classes.cardTitle}>
                      {status ? "読み終えました" : saving ? "保存しています" : "読み終えた"}
                    </Typography>
                    <Typography className={classes.cardHint}>
                      {status ? dateLabel : "タップすると、この章に読了の印が付きます"}
                    </Typography>
                  </Box>
                </ButtonBase>
                {next ? (
                  <ButtonBase className={classes.tailCard} onClick={() => openArticle(next.id)}>
                    <Box flex={1} minWidth={0}>
                      <Typography className={classes.cardHint}>次の章</Typography>
                      <Typography className={classes.cardTitle}>{next.title}</Typography>
                    </Box>
                    <Chevron />
                  </ButtonBase>
                ) : (
                  <ButtonBase className={classes.tailCard} onClick={handleBack}>
                    <Box flex={1}>
                      <Typography className={classes.cardHint}>このレベルの章へ</Typography>
                      <Typography className={classes.cardTitle}>
                        {stage.label}：{stage.title}
                      </Typography>
                    </Box>
                    <Chevron />
                  </ButtonBase>
                )}
              </Box>
              <Box className={classes.ask}>
                <Typography className={classes.eyebrow}>この章でつまずいたら</Typography>
                <Typography className={classes.askTitle}>プランナーに直接聞けます</Typography>
                <Typography className={classes.askDescription}>
                  あなたの条件に合うプランナーが、読んで分からなかったところに答えます。相談は無料です。
                </Typography>
                <CustomButton
                  tone="inverse"
                  buttonSize="M"
                  fullWidth
                  className={classes.askButton}
                  onClick={openConsultation}
                >
                  相性の良いプランナーを見る
                </CustomButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      {isLeadModalOpen && <ArticleScrollModal onClose={() => setLeadModalOpen(false)} />}
    </BaseTemplate>
  );
};
